// Запасной путь: человек снял системной камерой телефона и загружает готовый
// файл — ровно как это устроено в kids.
//
// Зачем он есть. Веб-рекордер новый, и на конкретной модели телефона он может
// подвести: старый iOS, отвалившийся WebCodecs, перегрев. Оставлять человека в
// такой ситуации без способа сдать работу нельзя — он снял, потратил смену, и
// «приложение не смогло» это не его проблема.
//
// Экран живёт в ОСНОВНОМ бандле и не тянет движок записи: запасной путь должен
// работать именно тогда, когда с движком что-то не так.
import { useState } from 'preact/hooks';
import { VISTA_MAX_CLIP_SECONDS, VISTA_PART_BYTES, type VistaTask } from '@weern/contracts';
import { api } from '../api';
import { taskTitle, type Lang, type T } from '../i18n';
import { finishCapture, openCapture, putPartRaw, abortCapture } from '../capture/vistaR2Stream';
import { useResource } from '../cache';

type Phase = 'idle' | 'uploading' | 'done' | 'failed';

/** Длительность и размеры читаем из самого файла — это единственное, что мы о
 *  нём достоверно знаем: снимала чужая камера, метаданных кодека у нас нет. */
function probe(file: File): Promise<{ durationSeconds: number; width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    const done = (d: number, w: number, h: number) => {
      URL.revokeObjectURL(url);
      resolve({ durationSeconds: Math.round(d) || 0, width: w || 0, height: h || 0 });
    };
    v.onloadedmetadata = () => done(v.duration, v.videoWidth, v.videoHeight);
    // Файл, который браузер не смог разобрать, всё равно отправляем: сервер
    // хранит байты как есть, а длительность проставит человек при разборе.
    v.onerror = () => done(0, 0, 0);
    v.src = url;
  });
}

export function UploadPage({ t, lang }: { t: T; lang: Lang }) {
  const tasksRes = useResource('tasks', () => api.tasks());
  const tasks: VistaTask[] = (tasksRes.data?.rows ?? []).filter((x) => x.active && x.priority === 'focus');
  const [taskSlug, setTaskSlug] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const slug = taskSlug || tasks[0]?.slug || '';

  const upload = async (file: File) => {
    setPhase('uploading');
    setProgress(0);
    setError(null);
    let captureId: string | null = null;
    try {
      const meta = await probe(file);
      if (meta.durationSeconds > VISTA_MAX_CLIP_SECONDS) {
        setError(t('upload.tooLong'));
        setPhase('failed');
        return;
      }
      const session = await openCapture({
        taskSlug: slug,
        // Ориентацию берём из самого кадра, а не из положения телефона в момент
        // загрузки: человек может загружать сидя за столом что угодно.
        orientation: meta.width >= meta.height ? 'landscape' : 'portrait',
        source: 'upload',
        fileName: file.name,
        contentType: file.type || 'video/mp4',
      });
      captureId = session.captureId;

      // Те же части по 8 MiB, что у рекордера, и так же последовательно: один
      // запрос в полёте — минимум памяти на телефоне и понятный прогресс.
      let part = session.nextPart;
      for (let offset = (part - 1) * VISTA_PART_BYTES; offset < file.size; offset += VISTA_PART_BYTES) {
        const chunk = file.slice(offset, Math.min(offset + VISTA_PART_BYTES, file.size));
        // Прогресс живёт ВНУТРИ части: у файла меньше 8 МиБ часть всего одна,
        // и без этого человек смотрел на «0%» до самого конца заливки.
        await putPartRaw(session.captureId, part, chunk, (sent) =>
          setProgress(Math.min(100, Math.round(((offset + sent) / file.size) * 100))),
        );
        part += 1;
        setProgress(Math.min(100, Math.round(((offset + chunk.size) / file.size) * 100)));
      }

      await finishCapture({
        captureId: session.captureId,
        durationSeconds: meta.durationSeconds,
        width: meta.width,
        height: meta.height,
        fps: 0,
        fileName: file.name,
      });
      setPhase('done');
    } catch {
      // Незавершённая загрузка обрывается явно: половина файла в бакете рано
      // или поздно попадёт в отгрузку как целый клип.
      if (captureId) await abortCapture(captureId, 'error').catch(() => {});
      setError(t('upload.failed'));
      setPhase('failed');
    }
  };

  if (phase === 'done') {
    return (
      <div class="panel wb-card">
        <h2>{t('shoot.done.title')}</h2>
        <p class="muted">{t('shoot.done.body')}</p>
        <button type="button" class="btn-primary" onClick={() => setPhase('idle')}>
          {t('upload.more')}
        </button>
      </div>
    );
  }

  return (
    <div class="panel wb-card">
      <h2>{t('upload.title')}</h2>
      <p class="muted">{t('upload.hint')}</p>

      <label class="field">
        <span>{t('shoot.topic')}</span>
        <select value={slug} onChange={(e) => setTaskSlug((e.target as HTMLSelectElement).value)}>
          {tasks.map((task) => (
            <option key={task.slug} value={task.slug}>
              {taskTitle(lang, task)}
            </option>
          ))}
        </select>
      </label>

      {phase === 'uploading' ? (
        <p class="muted">
          {t('upload.progress', { percent: progress })}
        </p>
      ) : (
        <label class="field">
          <span>{t('upload.file')}</span>
          <input
            type="file"
            accept="video/*"
            disabled={!slug}
            onChange={(e) => {
              const f = (e.target as HTMLInputElement).files?.[0];
              if (f) void upload(f);
            }}
          />
        </label>
      )}

      {error && <div class="strip bad">{error}</div>}
    </div>
  );
}
